import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, ReactNode, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/Button';
import { UserContext } from '../../contexts/UserContext';
import { T } from '../../translation/src';
import { AdminSectionLinks } from './AdminSectionLinks';

export type StatusTone = 'red' | 'orange' | 'green' | 'grey';

const TONE_CLASSES: Record<StatusTone, string> = {
  red: 'bg-error-red-200 text-error-red-700 dark:bg-error-red-700 dark:text-error-red-200',
  orange: 'bg-warning-yellow-200 text-warning-yellow-700 dark:bg-warning-yellow-700 dark:text-warning-yellow-200',
  green: 'bg-neon-green-100 text-neon-green-800 dark:bg-neon-green-900 dark:text-neon-green-200',
  grey: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

export const StatusPill: React.FC<{ tone: StatusTone; children: ReactNode }> = ({ tone, children }) => (
  <span className={classNames('inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium', TONE_CLASSES[tone])}>{children}</span>
);

export interface AdminListTab<Key extends string> {
  key: Key;
  label: ReactNode;
  count?: number;
  tone?: StatusTone;
}

interface AdminListPageProps<Key extends string> {
  title: ReactNode;
  tabs: AdminListTab<Key>[];
  activeTab: Key;
  onTabChange: (key: Key) => void;
  // Column headers and one <tr> per row, rendered into a shared table.
  columns: ReactNode[];
  rows: ReactNode;
  isEmpty: boolean;
  emptyText: ReactNode;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  // Extra controls on the right of the tabs (a refresh button).
  actions?: ReactNode;
}

// The shell of the admin center's follow-up lists (failed uploads, invoices): back to the admin
// center, status tabs with their counts, a table, and load more. Superadmins only.
const AdminListPage_ = <Key extends string>({ title, tabs, activeTab, onTabChange, columns, rows, isEmpty, emptyText, loading, hasMore, onLoadMore, actions }: AdminListPageProps<Key>) => {
  const currentUser = useContext(UserContext);
  if (!currentUser?.isSuperAdmin) return null;

  return (
    <div className="flex flex-col w-full h-full px-14 overflow-hidden dark:bg-[#313131] dark:text-white">
      <div className="flex items-center gap-3 w-full pt-8 text-xl">
        <Link to="/admin-center" className="text-gray-500 hover:text-black dark:text-gray-400 dark:hover:text-white" aria-label="admin center">
          <ArrowLeftIcon className="w-5 h-5" />
        </Link>
        {title}
        <AdminSectionLinks />
      </div>
      <hr className="my-4 border-t border-gray-300 dark:border-gray-400" />
      <div className="flex flex-wrap items-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => onTabChange(tab.key)}
            className={classNames(
              'flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors',
              tab.key === activeTab ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black' : 'border-gray-300 hover:border-gray-500 dark:border-gray-600 dark:hover:border-gray-400',
            )}
          >
            {tab.label}
            {tab.count != null && (tab.tone ? <StatusPill tone={tab.tone}>{tab.count}</StatusPill> : <span className="text-xs text-gray-500">{tab.count}</span>)}
          </button>
        ))}
        {actions && <div className="ml-auto">{actions}</div>}
      </div>
      <div className="mt-6 overflow-auto grow pb-8">
        {loading && isEmpty ? (
          <div className="text-sm text-gray-500">
            <T _str="loading..." />
          </div>
        ) : isEmpty ? (
          <div className="text-sm text-gray-500">{emptyText}</div>
        ) : (
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="text-xs text-gray-500 border-b border-gray-300 dark:border-gray-600">
                {columns.map((column, index) => (
                  <th key={index} className="py-2 pr-4 font-medium whitespace-nowrap">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        )}
        {hasMore && (
          <div className="flex justify-center py-4">
            <Button variant="tertiary" size="xs" onClick={onLoadMore} loading={loading}>
              <T _str="load more" swc />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const AdminListPage = memo(AdminListPage_) as typeof AdminListPage_;
